import { trackDetachedAudioWork, type AudioWorkContext } from '../../audioWorkBarrier';
import { getUnwrappedErrorName, isStoreHealthFailure } from '../../errors';
import { addChunk, addChunkMetadataOnly, getPipelineMode } from '../../persistenceV2';
import {
  armMemoryOnlyWrites,
  isMemoryOnlyArmed,
  isMemoryOnlyWriteError,
} from '../../persistenceV2/writeFence';
import { TooLowStorageError, type SessionChunk } from '../../types';
import { deserializeBlob, getErrorInfo } from '../../utils';
import { logStorePersistFailure } from '../lib/logStorePersistFailure';
import { logger } from '../lib/logger';
import { simpleRetry } from '../simpleRetry';
import { registerPersist } from './persistRegistry';
import { reportStorageDegradedOnce } from './storageDegradedReport';
import { chunkMeta, type GetState } from './storeTypes';

// A memory-only refusal is the start's storage-less event repeated once per
// chunk: expected, and loud enough at error level to bury the real faults.
const logChunkPersistFailure = async (
  event_type: string,
  recordingId: string,
  err: unknown
) => {
  const details = await getErrorInfo(err).catch(() => ({ message: String(err) }));
  logStorePersistFailure(
    { event_type, recording_id: recordingId, details },
    { memoryOnly: isMemoryOnlyWriteError(err) }
  );
};

/**
 * Storage that dies mid-recording never reaches the start's arming, so without
 * this every later chunk spends two retry ladders against a dead store. Arming
 * is idempotent and is its own latch: only the run that flips the fence reports.
 *
 * `isStoreHealthFailure`, never the wider `isStorageDegradation` the start
 * tolerates on: tolerance is broad, arming is narrow, everywhere. This latches
 * the rest of the consult to memory, so it needs evidence the store itself is
 * broken. The start arms on the same evidence plus the quota floor, which only
 * its own pre-flight check can raise.
 *
 * Either attempt's error is enough of that evidence: missing one costs the whole
 * consult two retry ladders per chunk, while the narrow name list already guards
 * the false-positive direction. A refusal never classifies, so it is never picked.
 */
const armMemoryOnlyAfterChunkWriteFailure = (
  get: GetState,
  recordingId: string,
  chunkErr: unknown,
  metadataErr: unknown
): void => {
  if (getPipelineMode() !== 'ledger') return;
  const storeHealthErr = [chunkErr, metadataErr].find(isStoreHealthFailure);
  if (storeHealthErr === undefined) return;
  if (isMemoryOnlyArmed()) return;
  armMemoryOnlyWrites();
  logger.warn({
    event_type: 'MEMORY_ONLY_ARMED_MID_RECORDING',
    details: {
      recordingId,
      errorName: getUnwrappedErrorName(storeHealthErr),
      memoryOnly: true,
    },
  });
  // Same port and same toast as an armed start, through the latch they share:
  // a start that already degraded must not warn the clinician twice.
  reportStorageDegradedOnce(
    get,
    recordingId,
    new TooLowStorageError('audio storage failed mid-recording; recording from memory')
  );
};

/** Starts persistence and upload together; capture end publishes terminal metadata. */
export const handleNewChunkV2 = async (
  get: GetState,
  chunk: SessionChunk,
  useTranscriptionServiceV2: boolean,
  context?: AudioWorkContext
) => {
  // Raw Blobs pass through; migrated legacy blobs are unwrapped.
  const blob = deserializeBlob(chunk.blob);

  const persist = (async () => {
    try {
      context?.throwIfStale();
      if (!blob) throw new Error('chunk blob missing at persist time');
      await simpleRetry(() => addChunk(chunkMeta(chunk), blob));
      context?.throwIfStale();
    } catch (err) {
      if (context && !context.isCurrent()) return;
      await logChunkPersistFailure('ADD_CHUNK_ERROR', chunk.recordingId, err);
      try {
        context?.throwIfStale();
        await simpleRetry(() => addChunkMetadataOnly(chunkMeta(chunk)));
      } catch (metadataErr) {
        if (context && !context.isCurrent()) return;
        await logChunkPersistFailure(
          'ADD_CHUNK_METADATA_ERROR',
          chunk.recordingId,
          metadataErr
        );
        // Both attempts are spent: the store, not this write, is the problem.
        armMemoryOnlyAfterChunkWriteFailure(get, chunk.recordingId, err, metadataErr);
      }
    }
  })();
  // Upload failure waits for this before settling durable status.
  registerPersist(chunk, persist);

  const upload = context
    ? get().processChunk(chunk, useTranscriptionServiceV2, context)
    : get().processChunk(chunk, useTranscriptionServiceV2);
  // The recorder tail settles at persist; the upload continues detached but
  // stays in the logout-drain set — stop() never waits on the network.
  trackDetachedAudioWork(upload, 'chunk-upload');
  await persist;
};
